import { z } from "zod";
import { defineStepConfig } from "@erp/workflows";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Payment scheduling mode configurations
 */
const ImmediateModeConfigSchema = z.object({
  mode: z.literal("immediate"),
});

const PayRunModeConfigSchema = z.object({
  mode: z.literal("payRun"),
  cron: z
    .string()
    .describe(
      "Cron expression for pay run schedule (e.g., '0 9 * * 5' for every Friday at 9am)",
    ),
  immediacy: z
    .enum(["asap", "latest"])
    .describe(
      "Schedule ASAP (next pay run) or latest (last pay run before due date)",
    ),
  bufferDays: z
    .number()
    .nonnegative()
    .prefault(0)
    .describe("Buffer days before due date to ensure payment arrives on time"),
  rounding: z
    .enum(["up", "down", "nearest"])
    .optional()
    .describe("How to round to nearest pay run when due date doesn't align"),
  timezone: z
    .string()
    .optional()
    .describe(
      "Timezone for cron evaluation (e.g., 'America/New_York'); defaults to organization timezone or UTC",
    ),
});

const LatestDateModeConfigSchema = z.object({
  mode: z.literal("latestDate"),
  bufferDays: z
    .number()
    .nonnegative()
    .prefault(0)
    .describe("Buffer days before due date to ensure payment arrives on time"),
});

/**
 * Schedule Payment Step
 *
 * Calculates and schedules payment dates for invoices based on configurable modes:
 * - Immediate: Initiate payment immediately based on received date
 * - PayRun: Schedule payment on cron-based pay run schedule
 * - LatestDate: Schedule payment to arrive before due date
 *
 * Payment method is retrieved from the supplier's configured payment methods.
 * Payment date calculations use invoice receivedAt and dueDate fields.
 *
 * Buffer days provide an additional safety margin before the due date for both
 * payRun and latestDate modes.
 *
 * Uses business days for processing time calculations
 * Considers payment method processing times (ACH: 2 days, FedNow: 0 days, Check: 5 days)
 */
export const schedulePaymentStep = defineStepConfig({
  type: "schedule_payment",
  name: "Schedule Payment",
  description:
    "Calculate and schedule payment date for invoice based on mode (immediate, pay run, or latest date)",
  category: "Utility",
  libraryModule: "AccountsPayable",

  inputSchema: z.object({
    apInvoice: entityOfType(SystemEntity.AP_INVOICE),
  }),

  outputSchema: z.object({}),

  configSchema: z.discriminatedUnion("mode", [
    ImmediateModeConfigSchema,
    PayRunModeConfigSchema,
    LatestDateModeConfigSchema,
  ]),

  emits: [] as const,
});
